import { useState } from 'react';
import type { SiteData } from '@/data/types';
import { resolveImagePath } from '@/utils/imageResolver';

interface HeaderProps {
  siteData: SiteData;
}

export function Header({ siteData }: HeaderProps) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  return (
    <header className="bg-gradient-to-r from-cyan-500 to-teal-600 shadow-lg relative">
      {/* Water texture overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-cyan-400/20 to-teal-500/20 opacity-50"></div>
      
      <div id="mainnavbar" className="relative z-10">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between py-4 px-4 sm:px-6 lg:px-8">
            {/* Logo */}
            <div className="flex items-center">
              <a href="/" className="flex items-center">
                <img
                  src={resolveImagePath(siteData.logo)}
                  alt={siteData.title}
                  className="h-12 w-auto"
                />
              </a>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-8">
              {siteData.navigation.map((item) => (
                <a
                  key={item.href}
                  href={item.href}
                  className="text-white hover:text-cyan-200 font-medium transition-colors duration-200"
                >
                  {item.text}
                </a>
              ))}
            </nav>

            {/* Phone & Mobile Menu Button */}
            <div className="flex items-center space-x-4">
              {/* Phone */}
              <div className="hidden sm:flex items-center">
                <a
                  href={`tel:${siteData.phone}`}
                  className="bg-white/20 text-white px-4 py-2 rounded-full hover:bg-white/30 font-semibold transition-all duration-200 backdrop-blur-sm"
                >
                  {siteData.phone}
                </a>
              </div>

              {/* Mobile menu button */}
              <button
                type="button"
                className="md:hidden inline-flex items-center justify-center p-2 rounded-md text-white hover:text-cyan-200 hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                aria-expanded="false"
              >
                <span className="sr-only">Open main menu</span>
                <svg
                  className={`${isMobileMenuOpen ? 'hidden' : 'block'} h-6 w-6`}
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
                <svg
                  className={`${isMobileMenuOpen ? 'block' : 'hidden'} h-6 w-6`}
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        <div className={`${isMobileMenuOpen ? 'block' : 'hidden'} md:hidden bg-teal-700/95 border-t border-teal-600`}>
          <nav className="px-4 pt-2 pb-4 space-y-1">
            {siteData.navigation.map((item) => (
              <a
                key={item.href}
                href={item.href}
                className="block px-3 py-2 text-base font-medium text-white hover:text-cyan-200 hover:bg-white/10 rounded-md"
              >
                {item.text}
              </a>
            ))}
            <a
              href={`tel:${siteData.phone}`}
              className="block px-3 py-2 text-base font-medium text-white hover:text-cyan-200 hover:bg-white/10 rounded-md sm:hidden"
            >
              {siteData.phone}
            </a>
          </nav>
        </div>
      </div>
    </header>
  );
}